/**
 * @file localization.ts
 * @description
 * -----------------------------------------------------
 * Archivo con funciones helper para la localización y enrutado
 * -----------------------------------------------------
 * @version 1.0.0a
 * @created 2025-06-02
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 *
 * */

import { locales, pathnames, Pathname } from "@/features/i18n";

/**
 * Verifica si un pathname está en una lista de pathnames.
 * @param {string} pathname - El pathname a verificar.
 * @param {Pathname[]} list - La lista de pathnames permitidos.
 * @returns {boolean} - `true` si el pathname está en la lista, `false` en caso contrario.
 */
export function isPathnameInList(pathname: string, list: Pathname[]): boolean {
  // Verifica si el pathname sin internacionalización está en la lista de permitidos
  return list.includes(getUninternationalizedPathname(pathname) as Pathname);
}

/**
 * Normaliza un pathname eliminando el prefijo de idioma si lo tiene y encuentra su versión sin traducción.
 * @param {string} pathname - El pathname a normalizar.
 * @returns {Pathname} - El pathname sin internacionalización.
 */
export function getUninternationalizedPathname(pathname: string): Pathname {
  // Encuentra si el pathname comienza con un identificador de idioma (locale)
  const locale = locales.find(
    (loc) => pathname.startsWith(`/${loc}/`) || pathname === `/${loc}`
  );

  // Normaliza el pathname eliminando el prefijo de idioma si lo tiene
  const normalizedPathname =
    (locale ? pathname.slice(locale.length + 1) : pathname) || "/";

  let uninternationalizedPathname = normalizedPathname;

  // Recorre los pathnames configurados para encontrar su versión sin traducción
  for (const [key, value] of Object.entries(pathnames)) {
    if (typeof value === "object") {
      for (const [k, v] of Object.entries(value)) {
        if (locale === k && v === uninternationalizedPathname) {
          uninternationalizedPathname = key;
        }
      }
    } else {
      if (value === uninternationalizedPathname) {
        uninternationalizedPathname = key;
      }
    }
  }

  return uninternationalizedPathname as Pathname;
}

export const getCurrentLocale = (locale: string): (typeof locales)[number] => {
  const index = locales.indexOf(locale as (typeof locales)[number]);
  return locales[index] || locales[0];
};

export const getNextLocale = (locale: string): (typeof locales)[number] => {
  const index = locales.indexOf(locale as (typeof locales)[number]);
  return locales[(index + 1) % locales.length];
};
